import { useEffect, useState } from "react";
import type { AbstractAgent } from "@ag-ui/client";

export interface RunError {
  message: string;
  // AG-UI run_id (== backend trace_id) of the failed run, captured at
  // RunStarted since RunErrorEvent doesn't carry it. Quoted to support.
  runId?: string;
}

// The message from a terminal RUN_ERROR. Kept as one standalone value rather
// than keyed to a message id: RUN_ERROR carries none, and the post-run reconcile
// rebuilds the message list. `useTurns` re-attaches it to the turn that failed,
// which is where the persisted marker lands after a reload. `useAgent` only
// wires onRunErrorEvent to end the run; without this the message is dropped
// entirely.
//
// Cleared the moment the next run is kicked off, so only the latest failure is
// ever shown. That has to happen client-side, at `onRunInitialized`: the
// composer stages the next user message and flips `isRunning` before the
// server's RunStarted arrives, and an error still set in that window would be
// stamped onto the new turn instead of the one that failed.
export function useRunError(agent: AbstractAgent): RunError | null {
  const [error, setError] = useState<RunError | null>(null);

  useEffect(() => {
    let runId: string | undefined;
    const sub = agent.subscribe({
      onRunInitialized: () => {
        runId = undefined;
        setError((prev) => (prev === null ? prev : null));
      },
      onRunStartedEvent: ({ event }) => {
        runId = event.runId;
      },
      onRunErrorEvent: ({ event }: { event: { message?: string } }) => {
        const message = event.message?.trim();
        if (!message) return;
        // Cancellation ends the run through its own "[cancelled]" marker, not a
        // RUN_ERROR — but guard defensively so a cancel never reads as a failure.
        if (message.toLowerCase().includes("cancel")) return;
        setError({ message, runId });
      },
    });
    return () => sub.unsubscribe();
  }, [agent]);

  return error;
}
